import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Function_This() {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">
        🧭 JavaScript Function <strong style={{ color: "red" }}>this</strong>{" "}
        Keyword
      </h1>

      <p className="tutorial-intro">
        The <code className="highlight_code">this</code> keyword refers to the
        object that is executing the current function. Its value depends on how
        the function is <strong>invoked</strong>, not where it’s defined.
      </p>
      {/* 🧠 Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem 2rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🧠 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code className="highlight_code">this</code> depends on how the
            function is called.
          </li>
          <li>
            Arrow functions don’t bind{" "}
            <code className="highlight_code">this</code> — they inherit it from
            the parent scope.
          </li>
          <li>
            In methods, <code className="highlight_code">this</code> refers to
            the object the method belongs to.
          </li>
          <li>
            Use <code className="highlight_code">call()</code>,{" "}
            <code className="highlight_code">apply()</code>, or{" "}
            <code className="highlight_code">bind()</code> to manually control{" "}
            <code className="highlight_code">this</code>.
          </li>
        </ul>
      </div>

      {/* 1️⃣ Global Context */}
      <div className="tutorial-section">
        <h3>
          1️⃣ <code className="highlight_code">this</code> in Global Context
        </h3>
        <p>
          In the global scope, <code className="highlight_code">this</code>{" "}
          refers to the <code className="highlight_code">window</code> object
          (in browsers).
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`console.log(this === window); // true (in browsers)`}
          />
          <TryItButton code={`console.log(this===window);`} />
        </div>
        <OutputBox>Output: true (in browsers)</OutputBox>
      </div>

      {/* 2️⃣ Inside a Regular Function */}
      <div className="tutorial-section">
        <h3>
          2️⃣ <code className="highlight_code">this</code> Inside a Regular
          Function
        </h3>
        <p>
          In regular functions, <code className="highlight_code">this</code>{" "}
          refers to <code className="highlight_code">undefined</code> in strict
          mode, or the global object otherwise.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function showThis() {
  console.log(this);
}
showThis();`}
          />
          <TryItButton
            code={`function showThis(){console.log(this);}showThis();`}
          />
        </div>
        <OutputBox>
          Output: undefined (in strict mode) or window (non-strict)
        </OutputBox>
      </div>

      {/* 3️⃣ Inside an Object Method */}
      <div className="tutorial-section">
        <h3>
          3️⃣ <code className="highlight_code">this</code> Inside an Object
          Method
        </h3>
        <p>
          When a function is called as a method,{" "}
          <code className="highlight_code">this</code> refers to the object that
          owns the method.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const user = {
  name: "Alice",
  greet: function() {
    console.log("Hello " + this.name);
  }
};

user.greet();`}
          />
          <TryItButton
            code={`const user={name:"Alice",greet:function(){console.log("Hello "+this.name);}};user.greet();`}
          />
        </div>
        <OutputBox>Output: Hello Alice</OutputBox>
      </div>

      {/* 4️⃣ In Constructor Functions */}
      <div className="tutorial-section">
        <h3>
          4️⃣ <code className="highlight_code">this</code> in Constructor
          Functions
        </h3>
        <p>
          When used inside a constructor,{" "}
          <code className="highlight_code">this</code> refers to the new object
          being created.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function Person(name) {
  this.name = name;
}

const p1 = new Person("Bob");
console.log(p1.name);`}
          />
          <TryItButton
            code={`function Person(name){this.name=name;}const p1=new Person("Bob");console.log(p1.name);`}
          />
        </div>
        <OutputBox>Output: Bob</OutputBox>
      </div>

      {/* 5️⃣ In Arrow Functions */}
      <div className="tutorial-section">
        <h3>
          5️⃣ <code className="highlight_code">this</code> in Arrow Functions
        </h3>
        <p>
          Arrow functions do <strong>not</strong> have their own{" "}
          <code className="highlight_code">this</code>. They inherit it from
          their enclosing (lexical) scope.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const person = {
  name: "Charlie",
  greet: () => {
    console.log("Hello " + this.name);
  }
};

person.greet();`}
          />
          <TryItButton
            code={`const person={name:"Charlie",greet:()=>{console.log("Hello "+this.name);}};person.greet();`}
          />
        </div>
        <OutputBox>
          Output: Hello undefined (arrow functions don’t bind this)
        </OutputBox>
      </div>

      {/* 6️⃣ Using call(), apply(), bind() */}
      <div className="tutorial-section">
        <h3>
          6️⃣ Setting <code className="highlight_code">this</code> Manually
        </h3>
        <p>
          The methods <code className="highlight_code">call()</code>,{" "}
          <code className="highlight_code">apply()</code>, and{" "}
          <code className="highlight_code">bind()</code> can explicitly set{" "}
          <code className="highlight_code">this</code>.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function introduce() {
  console.log("Hi, I'm " + this.name);
}

const user = { name: "David" };

introduce.call(user); // Using call()`}
          />
          <TryItButton
            code={`function introduce(){console.log("Hi, I'm "+this.name);}const user={name:"David"};introduce.call(user);`}
          />
        </div>
        <OutputBox>Output: Hi, I'm David</OutputBox>
      </div>

      {/* 🚀 Next Step */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem 2rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Step</h2>
        <p>
          Learn about <strong>Arrow Functions</strong> next to understand how
          they handle <code className="highlight_code">this</code> differently
          from traditional functions.
        </p>
      </div>
    </div>
  );
}
